import { DateTime } from 'luxon'

export type Environment = 'local' | 'test' | 'uat' | 'prod'

/**
 * Derives the deployment environment from the browser hostname.
 *
 * The reservation tool ships an identical bundle to every environment (there are
 * no build-time env vars), so the only runtime signal is the static-site subdomain,
 * e.g. `test.reservation-tool.gambytservices.com`. Anything unrecognized falls back
 * to `prod` so an unknown production hostname can never accidentally opt into a
 * preview; `localhost` is treated as `local`.
 */
export function getEnvironment(
  hostname: string = typeof window !== 'undefined' ? window.location.hostname : '',
): Environment {
  if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '') {
    return 'local'
  }
  if (hostname.startsWith('test.')) {
    return 'test'
  }
  if (hostname.startsWith('uat.')) {
    return 'uat'
  }
  return 'prod'
}

/**
 * When a scheduled change takes effect.
 *
 * Either a single ISO date (`YYYY-MM-DD`) applied to every environment, or a
 * per-environment map so environments can be cut over independently (e.g. preview
 * a value in `test` before the `prod` cutover). Environments omitted from the map
 * never receive the change.
 */
export type EffectiveDate = string | Partial<Record<Environment, string>>

export type ScheduledChange<T> = {
  /** Takes effect at 00:00 in the provided timezone on the resolved date. */
  effectiveDate: EffectiveDate
  value: T
}

function effectiveDateForEnvironment(
  effectiveDate: EffectiveDate,
  environment: Environment,
): string | undefined {
  return typeof effectiveDate === 'string' ? effectiveDate : effectiveDate[environment]
}

/**
 * Returns `current` until a scheduled change's effectiveDate has arrived
 * (evaluated in the provided timezone for the given environment), then the value of the
 * latest change that has taken effect.
 *
 * `now` and `environment` are injectable so callers can be tested without mocking
 * the global clock or the browser location.
 */
export function resolveScheduledValue<T>(
  current: T,
  changes: ScheduledChange<T>[],
  timezone: string,
  now: DateTime = DateTime.now(),
  environment: Environment = getEnvironment(),
): T {
  const nowInZone = now.setZone(timezone)

  const active = changes
    .map((change) => ({
      value: change.value,
      date: effectiveDateForEnvironment(change.effectiveDate, environment),
    }))
    .filter((change): change is { value: T; date: string } => change.date !== undefined)
    .map((change) => ({
      value: change.value,
      at: DateTime.fromISO(change.date, { zone: timezone }).startOf('day'),
    }))
    .filter((change) => nowInZone >= change.at)
    .sort((a, b) => b.at.toMillis() - a.at.toMillis())

  return active.length > 0 ? active[0].value : current
}
